import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ConversationBillingBadge from '@/components/page-components/project/agents/conversations/ConversationBillingBadge';
import { getConversationBillingState } from '@/utils/conversationBilling';
import {
  selectAgents,
  selectAgentsStatus,
  fetchAgents,
  updateContact,
} from '@/redux/agentsSlice';
import { Conversation } from '@/types/Conversation';
import { Message } from '@/types/Message';
import { Contact } from '@/types/Contact';
import { Button } from '@/components/ui/button';
import {
  Download,
  Play,
  Pause,
  Volume2,
  RotateCcw,
  RotateCw,
  SquareArrowOutUpRight,
  Calendar,
  ExternalLink,
  ArrowUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ContactDisplay from '@/components/common/ContactDisplay';
import ContactSheet from '@/components/page-components/project/agents/contacts/ContactSheet';
import { useToast } from '@/hooks/useToast';
import type { AppDispatch } from '@/redux/store';
import {
  enhancedTranscriptApi,
  type EnhancedTranscript,
  conversationSummariesApi,
  type ConversationSummary,
  conversationAudioApi,
  bookingsApi,
  servicesApi,
  conversationCallbackApi,
  type ConversationCallbackRequest,
} from '@/services/api';
import { Booking } from '@/types/Booking';
import type { Service } from '@/types/Service';
import { AppointmentCard } from '@/components/page-components/project/agents/calendar/AppointmentCard';
import { PhoneOutgoing } from 'lucide-react';
import { hasCrm } from '@/utils/crmUtils';
import { useDateFormat } from '@/hooks/useDateFormat';

/** Slack in px for "at the bottom" — exact equality never holds with
 *  fractional scroll heights, zoom, or momentum scrolling. */
const SCROLL_BOTTOM_THRESHOLD = 24;

const ConversationPage: React.FC = () => {
  const { t } = useTranslation();
  const df = useDateFormat();
  // The header overlays the content, so both columns have to reserve its
  // height. It is not a constant — it grows when the audio player loads and
  // reflows at every breakpoint — so measure it rather than hardcode it.
  //
  // Held as state behind a callback ref rather than a useRef: this component
  // early-returns a loading skeleton and a not-found state before the header
  // ever renders, so an effect keyed on [] would run against a null ref and
  // never re-run once the real header mounted. A callback ref fires on attach,
  // whenever that happens to be.
  const [headerNode, setHeaderNode] = useState<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const [isTranscriptAtBottom, setIsTranscriptAtBottom] = useState(false);

  const handleTranscriptScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const scrollableDistance = el.scrollHeight - el.clientHeight;
    // Guard on there being something to scroll: a transcript shorter than the
    // column sits at its bottom by definition, and would otherwise show a
    // "back to top" button that does nothing.
    const canScroll = scrollableDistance > SCROLL_BOTTOM_THRESHOLD;
    const distanceFromBottom = scrollableDistance - el.scrollTop;
    setIsTranscriptAtBottom(
      canScroll && distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD
    );
  };

  const scrollTranscriptToTop = () => {
    transcriptScrollRef.current?.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  };

  useEffect(() => {
    if (!headerNode) return;

    // getBoundingClientRect, not entry.contentRect: contentRect is the content
    // box and would drop the header's own p-2/md:p-4 padding, leaving the
    // reserved space short by 16-32px.
    const measure = () =>
      setHeaderHeight(headerNode.getBoundingClientRect().height);

    const observer = new ResizeObserver(measure);
    observer.observe(headerNode);
    measure();

    return () => observer.disconnect();
  }, [headerNode]);

  const { toast } = useToast();
  const { agent_id, conversation_id } = useParams<{
    agent_id: string;
    conversation_id: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const agents = useSelector(selectAgents);
  const agentsStatus = useSelector(selectAgentsStatus);
  const currentAgent = agents.find(a => a.id === Number(agent_id));
  const crmEnabled = hasCrm(currentAgent);

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isLoadingEnhancedTranscript, setIsLoadingEnhancedTranscript] =
    useState(false);
  const [conversationSummary, setConversationSummary] =
    useState<ConversationSummary | null>(null);
  const [isLoadingConversationSummary, setIsLoadingConversationSummary] =
    useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Contact viewing state
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false);
  const [viewingContact, setViewingContact] = useState<Contact | null>(null);
  const [linkedBooking, setLinkedBooking] = useState<Booking | null>(null);
  const [linkedServices, setLinkedServices] = useState<Service[]>([]);
  const [callbackRequest, setCallbackRequest] =
    useState<ConversationCallbackRequest | null>(null);

  // Function to fetch enhanced transcript from API
  const fetchEnhancedTranscript = React.useCallback(
    async (conversationId: string) => {
      try {
        setIsLoadingEnhancedTranscript(true);
        const data =
          await enhancedTranscriptApi.fetchEnhancedTranscript(conversationId);
        return data;
      } catch (error) {
        return null;
      } finally {
        setIsLoadingEnhancedTranscript(false);
      }
    },
    []
  );

  // Function to fetch conversation summary from API
  const fetchConversationSummary = React.useCallback(
    async (conversationId: string) => {
      try {
        setIsLoadingConversationSummary(true);
        const data =
          await conversationSummariesApi.fetchConversationSummary(
            conversationId
          );
        return data;
      } catch (error) {
        return null;
      } finally {
        setIsLoadingConversationSummary(false);
      }
    },
    []
  );

  // Function to transform enhanced transcript segments to Message format
  const transformEnhancedTranscriptToMessages = React.useCallback(
    (transcript: EnhancedTranscript): Message[] => {
      return enhancedTranscriptApi.transformEnhancedTranscriptToMessages(
        transcript
      );
    },
    []
  );

  // Fetch agents if not loaded
  useEffect(() => {
    if (agentsStatus === 'idle' && agents.length === 0) {
      dispatch(fetchAgents());
    }
  }, [dispatch, agentsStatus, agents.length]);

  // Find the agent and conversation, and fetch enhanced transcript
  useEffect(() => {
    if (!agent_id || !conversation_id) {
      navigate('/home');
      return;
    }

    // Wait for agents to finish loading
    if (agentsStatus === 'loading' || agentsStatus === 'idle') {
      return;
    }

    if (agentsStatus === 'failed' || agents.length === 0) {
      navigate('/home');
      return;
    }

    const agent = agents.find(a => a.id?.toString() === agent_id);
    if (!agent) {
      navigate('/home');
      return;
    }

    const foundConversation = agent.conversations?.find(
      c => c.id.toString() === conversation_id
    );
    // Deliberately no redirect: bouncing back to the agent page made a missing
    // conversation look like a dead click, because an agent without an active
    // plan lands on its settings tab rather than the list the click came from.
    // Falling through to the not-found state below says what happened instead.
    if (!foundConversation) {
      setIsLoading(false);
      return;
    }

    setConversation(foundConversation);

    // Fetch enhanced transcript and set messages
    const loadConversationData = async () => {
      try {
        // First try to fetch enhanced transcript
        const enhancedTranscriptData =
          await fetchEnhancedTranscript(conversation_id);

        if (enhancedTranscriptData) {
          // Use enhanced transcript if available
          const transformedMessages = transformEnhancedTranscriptToMessages(
            enhancedTranscriptData
          );
          setMessages(transformedMessages);
        } else {
          // Fall back to original messages if no enhanced transcript
          setMessages(foundConversation.messages || []);
        }

        // Fetch conversation summary
        const summaryData = await fetchConversationSummary(conversation_id);
        if (summaryData) {
          setConversationSummary(summaryData);
        }
      } catch (error) {
        // Fall back to original messages on error
        setMessages(foundConversation.messages || []);
      } finally {
        setIsLoading(false);
      }
    };

    loadConversationData();
  }, [
    agent_id,
    conversation_id,
    agents,
    agentsStatus,
    navigate,
    fetchEnhancedTranscript,
    transformEnhancedTranscriptToMessages,
    fetchConversationSummary,
  ]);

  useEffect(() => {
    if (!conversation_id) {
      setCallbackRequest(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const request = await conversationCallbackApi.fetchByConversation(
          Number(conversation_id)
        );
        if (!cancelled) setCallbackRequest(request);
      } catch {
        // The owner losing sight of the reason is worse than a silent miss
        // here, but it is not worth failing the page over.
        if (!cancelled) setCallbackRequest(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversation_id]);

  useEffect(() => {
    if (!crmEnabled || !agent_id || !conversation_id) {
      setLinkedBooking(null);
      return;
    }

    let cancelled = false;

    const fetchLinkedBooking = async () => {
      try {
        const booking = await bookingsApi.fetchBookingByConversationId(
          Number(agent_id),
          Number(conversation_id)
        );
        if (cancelled) return;
        setLinkedBooking(booking);

        // Only worth a second call once we know there is a card to name a
        // service on; the conversation page does not otherwise need them.
        if (booking) {
          const services = await servicesApi.fetchServices(Number(agent_id));
          if (!cancelled) setLinkedServices(services);
        }
      } catch {
        if (!cancelled) setLinkedBooking(null);
      }
    };

    void fetchLinkedBooking();

    return () => {
      cancelled = true;
    };
  }, [agent_id, conversation_id, crmEnabled]);

  // Reset audio when switching conversations so cached URLs don't play the wrong file
  useEffect(() => {
    setAudioUrl(null);
    setAudioError(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoadingAudio(false);

    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
  }, [conversation_id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleLoadAudio = async () => {
    if (!conversation_id) return;

    try {
      setIsLoadingAudio(true);
      setAudioError(null); // Clear any previous errors

      const data =
        await conversationAudioApi.generatePresignedUrl(conversation_id);

      if (data?.presignedUrl) {
        // Set the audio URL for the player
        setAudioUrl(data.presignedUrl);
      } else {
        // No presigned URL returned - likely no audio available
        const errorMessage = 'No audio available for this conversation';
        setAudioError(errorMessage);
        toast({
          title: 'Audio Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to load audio:', error);

      // Handle different types of errors
      let errorMessage = 'An unexpected error occurred';
      if (error instanceof Error) {
        if (
          error.message.includes('404') ||
          error.message.includes('not found')
        ) {
          errorMessage = 'No audio file found for this conversation';
        } else if (
          error.message.includes('403') ||
          error.message.includes('forbidden')
        ) {
          errorMessage = 'Access denied to audio file';
        } else if (
          error.message.includes('timeout') ||
          error.message.includes('network')
        ) {
          errorMessage = 'Network error. Please try again';
        } else {
          errorMessage = 'Failed to load audio. Please try again';
        }
      }
      setAudioError(errorMessage);
      toast({
        title: 'Audio Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const handleDownloadAudio = async () => {
    if (!audioUrl) return;

    try {
      setIsDownloading(true);

      // Fetch the audio file as a blob
      const response = await fetch(audioUrl);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch audio file: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();

      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `conversation-${conversation_id}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);

      toast({
        title: 'Download Complete',
        description: 'Audio file downloaded successfully',
        variant: 'default',
      });
    } catch (error) {
      console.error('Failed to download audio:', error);
      toast({
        title: 'Download Error',
        description: 'Failed to download audio file. Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, currentTime - 10);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, currentTime + 10);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to handle contact updates
  const handleContactUpdate = async (contactId: number, name: string) => {
    try {
      // Use the proper Redux action to update the contact
      await dispatch(updateContact({ contactId, updates: { name } })).unwrap();

      // Close the contact sheet after successful update
      setIsContactSheetOpen(false);
      setViewingContact(null);

      toast({
        title: 'Contact updated',
        description: 'Contact updated successfully.',
      });
    } catch (error) {
      console.error('Failed to update contact:', error);
      // Keep the sheet open on error so user can retry
      throw error; // Re-throw to let ContactSheet handle the error
    }
  };

  // Function to open ContactSheet in view mode
  const handleOpenContact = () => {
    if (conversation && agent_id) {
      const agent = agents.find(a => a.id?.toString() === agent_id);
      if (agent?.contacts) {
        const contact = agent.contacts.find(
          c => c.caller_id === conversation.caller_id
        );
        if (contact) {
          setViewingContact(contact);
          setIsContactSheetOpen(true);
        }
      }
    }
  };

  if (
    isLoading ||
    isLoadingEnhancedTranscript ||
    isLoadingConversationSummary
  ) {
    return (
      <div className="flex flex-col h-full">
        {/* Header Skeleton */}
        <div className="flex-shrink-0">
          <Skeleton className="h-16 md:h-20 w-full rounded-lg mb-4 md:mb-6" />
        </div>

        {/* Content Skeleton - Matching actual layout */}
        <div className="flex overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 md:gap-6 p-0 md:p-6 w-full">
            {/* Left Column - Summary Skeleton */}
            <div className="lg:col-span-1">
              <Card className="h-fit bg-card rounded-none md:rounded-lg">
                <CardHeader className="p-2 md:p-3">
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardContent className="space-y-4 p-2 md:p-6">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Messages Skeleton */}
            <div className="lg:col-span-1 h-full">
              <Card className="h-full flex flex-col bg-card rounded-none md:rounded-lg">
                <CardContent className="p-2 md:p-4 space-y-4">
                  <div className="flex justify-start">
                    <Skeleton className="h-16 w-3/4 rounded-lg" />
                  </div>
                  <div className="flex justify-end">
                    <Skeleton className="h-16 w-2/3 rounded-lg" />
                  </div>
                  <div className="flex justify-start">
                    <Skeleton className="h-12 w-1/2 rounded-lg" />
                  </div>
                  <div className="flex justify-end">
                    <Skeleton className="h-20 w-4/5 rounded-lg" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-muted-foreground">
          {t('error.conversation_not_found.translation')}
        </p>
        <Button onClick={handleBack} className="mt-4">
          {t('common.back.translation')}
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full">
      {/* Overlaid header. It has to sit *over* the content rather than above
          it in flow: the frosted glass only reads as glass when the transcript
          passes underneath, and as a flex sibling nothing ever did. */}
      <Card
        ref={setHeaderNode}
        glass
        className="absolute inset-x-0 top-0 z-30 flex flex-col p-2 md:p-4 gap-2 rounded-lg"
      >
        {/* First Row: Conversation Title + Load Audio Button */}
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base md:text-xl flex items-center gap-1 md:gap-2 flex-wrap">
              {t('agent.conversations.chat.title.translation', {
                caller_id: '',
              })}
              <ContactDisplay
                callerId={conversation.caller_id}
                agentId={Number(agent_id)}
                contacts={
                  agents.find(a => a.id?.toString() === agent_id)?.contacts
                }
                variant="inline"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleOpenContact}
                className="h-8 w-8 flex-shrink-0"
                title={t('contacts.open_contact.translation', 'Open Contact')}
              >
                <SquareArrowOutUpRight className="w-3 h-3 md:w-4 md:h-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs md:text-sm text-muted-foreground">
                {df.date(conversation.created_at)} •{' '}
                {df.time(conversation.created_at)} •{' '}
                {conversation.duration
                  ? t(
                      'agent.conversations.table.duration.minutes.translation',
                      {
                        minutes: Math.floor(conversation.duration / 60),
                        seconds: conversation.duration % 60,
                      }
                    )
                  : t('agent.conversations.chat.duration_unknown.translation')}
              </p>
              <ConversationBillingBadge
                state={getConversationBillingState(
                  conversation,
                  currentAgent?.subscription
                )}
              />
            </div>
          </div>

          {/* Load Audio Button - Show on same row when audio is not loaded */}
          {!audioUrl && !audioError && (
            <Button
              onClick={handleLoadAudio}
              disabled={isLoadingAudio}
              variant="tertiary"
              size="sm"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm flex-shrink-0"
            >
              <Play className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden sm:inline">
                {isLoadingAudio
                  ? t('agent.conversations.chat.audio.loading.translation')
                  : t('agent.conversations.chat.audio.load.translation')}
              </span>
            </Button>
          )}

          {/* Error State - Show retry button on same row */}
          {audioError && (
            <Button
              onClick={handleLoadAudio}
              disabled={isLoadingAudio}
              variant="secondary"
              size="sm"
              className="text-xs flex-shrink-0"
            >
              {isLoadingAudio
                ? t('agent.conversations.chat.audio.retrying.translation')
                : t('agent.conversations.chat.audio.retry.translation')}
            </Button>
          )}
        </div>

        {/* Second Row: Audio Controls (only when audio is loaded) */}
        {(audioUrl || audioError) && (
          <div className="flex items-center gap-1 md:gap-2 w-full">
            {/* Download Button - Only show when audio is loaded */}
            {audioUrl && !audioError && (
              <Button
                variant="tertiary"
                onClick={handleDownloadAudio}
                disabled={isDownloading}
                size="sm"
                className="flex items-center gap-1 md:gap-2 text-xs md:text-sm flex-shrink-0"
              >
                <Download className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">
                  {isDownloading
                    ? t(
                        'agent.conversations.chat.audio.downloading.translation'
                      )
                    : t('agent.conversations.chat.audio.download.translation')}
                </span>
              </Button>
            )}

            {/* Audio Player Controls */}
            {audioUrl && !audioError && (
              <div className="flex items-center gap-1 md:gap-2 bg-card rounded-lg px-1.5 md:px-3 py-1 md:py-1.5 flex-1 min-w-0">
                <audio
                  key={conversation_id}
                  ref={audioRef}
                  src={audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                  onError={e => {
                    console.error('Audio playback error:', e);
                    const errorMessage =
                      'Failed to play audio. The file may be corrupted or unsupported.';
                    setAudioError(errorMessage);
                    toast({
                      title: 'Playback Error',
                      description: errorMessage,
                      variant: 'destructive',
                    });
                  }}
                  preload="metadata"
                />

                {/* Play/Pause Button */}
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={togglePlayPause}
                  className="h-7 w-7 md:h-8 md:w-8 flex-shrink-0"
                >
                  {isPlaying ? (
                    <Pause className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  ) : (
                    <Play className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  )}
                </Button>

                {/* Time Slider with Skip Buttons */}
                <div className="flex items-center gap-0.5 md:gap-1.5 flex-1 min-w-0">
                  {/* Skip backward - hidden on mobile */}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={skipBackward}
                    className="h-6 w-6 md:h-7 md:w-7 hidden sm:flex flex-shrink-0"
                    title={t(
                      'agent.conversations.chat.audio.skip_back.translation'
                    )}
                  >
                    <RotateCcw className="w-3 h-3 md:w-3.5 md:h-3.5" />
                  </Button>
                  <span className="text-[10px] md:text-xs text-muted-foreground flex-shrink-0 hidden sm:inline">
                    {formatTime(currentTime)}
                  </span>
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleSeek}
                    className="flex-1 h-1 bg-muted rounded-full appearance-none cursor-pointer min-w-0"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / Math.max(duration, 1)) * 100}%, #e2e8f0 ${(currentTime / Math.max(duration, 1)) * 100}%, #e2e8f0 100%)`,
                    }}
                  />
                  <span className="text-[10px] md:text-xs text-muted-foreground flex-shrink-0 hidden sm:inline">
                    {formatTime(duration)}
                  </span>
                  {/* Skip forward - hidden on mobile */}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={skipForward}
                    className="h-6 w-6 md:h-7 md:w-7 hidden sm:flex flex-shrink-0"
                    title={t(
                      'agent.conversations.chat.audio.skip_forward.translation'
                    )}
                  >
                    <RotateCw className="w-3 h-3 md:w-3.5 md:h-3.5" />
                  </Button>
                </div>

                {/* Volume Control - Only on larger screens */}
                <div className="flex items-center gap-1 md:gap-2 hidden lg:flex flex-shrink-0">
                  <Volume2 className="w-3 h-3 md:w-4 md:h-4 text-muted-foreground" />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-12 md:w-16 h-1 bg-muted rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${volume * 100}%, #e2e8f0 ${volume * 100}%, #e2e8f0 100%)`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Fills the full height so the transcript column can start behind the
          header. 2fr/3fr rather than an even split: the summary is short, the
          transcript is the thing worth reading. */}
      <div className="grid h-full min-h-0 w-full grid-cols-1 gap-0 p-0 md:gap-6 md:p-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        {/* Left Column - AI Summary. Static: it is short enough never to
              need its own scrollbar, so it stays put while the transcript
              moves. Cleared past the header rather than sliding under it. */}
        <div
          className="lg:col-span-1 overflow-y-auto min-h-0"
          style={{ paddingTop: headerHeight }}
        >
          <Card className="h-fit bg-card rounded-lg">
            <CardHeader className="px-2 md:px-3 pb-2 md:pb-3">
              <CardTitle>
                {t(
                  'agent.conversations.summary.conversation_summary.translation'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary */}
              <div>
                {conversationSummary ? (
                  <div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {conversationSummary.summary_text}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t(
                        'agent.conversations.summary.no_summary.translation',
                        'No summary available for this conversation.'
                      )}
                    </p>
                  </div>
                )}
              </div>

              {callbackRequest && (
                <div className="pt-2">
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <PhoneOutgoing className="h-3.5 w-3.5" aria-hidden />
                    {t(
                      'agent.conversations.summary.callback.translation',
                      'Callback requested'
                    )}
                  </p>
                  <p className="rounded-lg bg-card p-3 text-sm">
                    {callbackRequest.reason ||
                      t(
                        'agent.conversations.summary.callback_no_reason.translation',
                        'The caller asked to be called back by a person. The agent did not record a reason.'
                      )}
                  </p>
                </div>
              )}

              {crmEnabled && linkedBooking && (
                <div className="pt-2">
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                    {t(
                      'agent.conversations.summary.appointment.translation',
                      'Appointment'
                    )}
                  </p>
                  <AppointmentCard
                    booking={linkedBooking}
                    service={
                      linkedServices.find(
                        service => service.id === linkedBooking.service_id
                      ) ?? null
                    }
                    contacts={currentAgent?.contacts ?? []}
                    showDate
                  />
                  <Link
                    to={`/agents/${agent_id}?tab=calendar&booking=${linkedBooking.id}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    {t(
                      'agent.conversations.summary.view_appointment.translation',
                      'Open in calendar'
                    )}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Messages. The scroller owns the header clearance and
            the Card sits inside it, so the card travels with its own content:
            its top edge starts level with the summary card and then slides up
            under the glass as you scroll, instead of staying pinned while the
            bubbles ride out over the page background. min-h-full keeps the
            surface filling the column when the transcript is short. */}
        <div className="lg:col-span-1 relative h-full min-h-0">
          <div
            ref={transcriptScrollRef}
            onScroll={handleTranscriptScroll}
            className="h-full min-h-0 overflow-y-auto"
            style={{ paddingTop: headerHeight }}
          >
            <Card className="min-h-full rounded-lg">
              <div className="p-4 md:p-6">
                {/* Moved inside the scroller: the title scrolls away under
                        the glass with the messages instead of pinning a strip
                        of opaque card above them. */}
                <CardHeader className="px-0 pb-2 pt-0 md:pb-3">
                  <CardTitle>
                    {t('agent.conversations.chat.transcript_title.translation')}
                  </CardTitle>
                </CardHeader>
                <div className="flex flex-col gap-4 max-w-4xl mx-auto">
                  {messages.length === 0 ? (
                    <div className="flex justify-center items-center h-full text-muted-foreground">
                      {t('agent.conversations.no_messages.translation')}
                    </div>
                  ) : (
                    <>
                      {messages.map(message => {
                        const roleLower = message.role.toLowerCase();
                        return (
                          <div
                            key={message.id}
                            className={`flex ${
                              roleLower === 'user'
                                ? 'justify-end'
                                : 'justify-start'
                            } mb-2`}
                          >
                            <div
                              className={`p-3 rounded-lg max-w-md lg:max-w-lg ${
                                roleLower === 'user'
                                  ? 'bg-conversation-light dark:bg-conversation-dark-secondary text-primary-foreground'
                                  : roleLower === 'system'
                                    ? 'bg-muted text-foreground'
                                    : 'bg-muted text-foreground'
                              }`}
                            >
                              <div className="text-xs opacity-70 mb-1">
                                {message.role.toUpperCase()}
                              </div>
                              <div className="text-sm">{message.content}</div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Sibling of the scroller, not a child, so it stays put instead of
              riding the transcript. Kept mounted and faded rather than
              conditionally rendered so it can animate both ways;
              pointer-events-none stops the hidden state from swallowing
              clicks on the messages underneath. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={scrollTranscriptToTop}
            aria-hidden={!isTranscriptAtBottom}
            tabIndex={isTranscriptAtBottom ? 0 : -1}
            className={cn(
              'glass-surface glass-strong absolute bottom-4 right-4 z-20 gap-1.5 rounded-full px-3 text-xs text-foreground transition-[opacity,transform] duration-200 hover:text-foreground',
              isTranscriptAtBottom
                ? 'translate-y-0 opacity-100'
                : 'pointer-events-none translate-y-2 opacity-0'
            )}
          >
            <ArrowUp className="h-3.5 w-3.5" />
            {t('agent.conversations.chat.scroll_to_top.translation')}
          </Button>
        </div>
      </div>

      {/* Contact View Sheet */}
      <ContactSheet
        contact={viewingContact}
        isOpen={isContactSheetOpen}
        onClose={() => {
          setIsContactSheetOpen(false);
          setViewingContact(null);
        }}
        onSave={handleContactUpdate}
        conversations={
          agents.find(a => a.id?.toString() === agent_id)?.conversations || []
        }
        agentId={Number(agent_id)}
        autoEdit={false}
        context="conversation"
        currentConversationId={conversation_id}
      />
    </div>
  );
};

export default ConversationPage;
